import type { MessageReference, Uuid } from '@liniya/api-client';
import { encodeAttachmentBody, encodeMessageReference, newUuid, type UploadFile } from '@liniya/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { api } from '../api';
import { createPendingMessage, usePendingStore } from '../store/pending-messages';
import { notify } from '../store/ui';
import { realtimeCache } from '../realtime/handlers';
import { useRealtime } from '../realtime/provider';
import { queryKeys } from '../query-keys';

export interface OutgoingMessage {
  chatId: Uuid;
  deviceId: Uuid;
  text: string;
  reference: MessageReference | null;
  attachment?: { file: UploadFile; fileName: string; previewUrl: string } | undefined;
  /** Reuse the id of a failed attempt so a retry is not a second message. */
  messageId?: Uuid;
}

/**
 * Sending, from the user's point of view.
 *
 * The message appears the instant Send is pressed, carrying an id this client
 * generated. That id is also the server's idempotency key, so the optimistic
 * bubble and the confirmed message are the same message rather than two things
 * that have to be matched up afterwards — and a retry after a timeout cannot
 * produce a duplicate.
 *
 * The socket is tried first because it is already open and gives the fastest
 * acknowledgement. HTTP is not a lesser path: it carries the identical payload
 * to the identical endpoint, so a message sent while the socket is down is in
 * no way second-class.
 */
export function useSendMessage() {
  const queryClient = useQueryClient();
  const { client } = useRealtime();

  return useCallback(
    async (outgoing: OutgoingMessage): Promise<void> => {
      // Read the store imperatively: sending must not make every composer
      // keystroke re-render because some other chat's upload advanced.
      const pending = usePendingStore.getState();
      // Not `crypto.randomUUID`: Hermes has no Web Crypto, and the id is also
      // the server's idempotency key, so it has to exist on both clients.
      const messageId = outgoing.messageId ?? newUuid();
      const { chatId, deviceId, reference, attachment } = outgoing;
      const text = outgoing.text.trim();
      if (!text && !attachment) return;

      pending.add(
        createPendingMessage({
          messageId,
          chatId,
          content: text,
          reference,
          ...(attachment ? { attachmentName: attachment.fileName } : {}),
          ...(attachment ? { localPreviewUrl: attachment.previewUrl } : {}),
        }),
      );

      try {
        let attachmentIds: Uuid[] = [];
        if (attachment) {
          const upload = api.media.upload({
            file: attachment.file,
            fileName: attachment.fileName,
            onProgress: (percent) =>
              pending.update(chatId, messageId, { uploadProgress: percent }),
          });
          // Registered before the wait, so the bubble can offer a way out of a
          // large file on a slow uplink instead of only a percentage.
          pending.registerCancel(chatId, messageId, upload.cancel);
          const stored = await upload.promise;
          attachmentIds = [stored.id];
        }

        const body = attachment
          ? encodeAttachmentBody(attachment.fileName, text)
          : text || reference?.excerpt || '';
        const content = reference ? encodeMessageReference(body, reference) : body;

        const payload = {
          message_id: messageId,
          chat_id: chatId,
          sender_device_id: deviceId,
          protocol: 'LINE_SERVER_V1' as const,
          content,
          attachment_ids: attachmentIds,
        };

        let acknowledged = false;
        if (client?.isReady()) {
          try {
            await client.send('message.send', payload);
            // The acknowledgement carries routing metadata only; the message
            // itself follows as a durable event and lands in the cache there,
            // which is also what clears the optimistic copy.
            acknowledged = true;
          } catch {
            // Fall through to HTTP: the socket may have dropped mid-send, and
            // the identical id makes the second attempt safe.
          }
        }

        if (!acknowledged) {
          const response = await api.messages.send(payload);
          realtimeCache.upsertMessage(queryClient, response.message);
          pending.remove(chatId, messageId);
          await queryClient.invalidateQueries({ queryKey: queryKeys.chats });
        }
      } catch (error) {
        // A cancelled upload already removed the message; reporting a failure
        // for something the person deliberately stopped would be noise.
        if (!pending.get(chatId, messageId)) return;
        pending.update(chatId, messageId, {
          state: 'failed',
          error: error instanceof Error ? error.message : 'Не удалось отправить сообщение.',
        });
        notify('Сообщение не отправлено. Нажмите «Повторить».', 'error');
      }
    },
    [client, queryClient],
  );
}

/**
 * Re-send a message that failed.
 *
 * The original id is reused on purpose: if the first attempt actually reached
 * the server before the connection broke, the retry returns that same message
 * instead of posting it twice.
 */
export function useRetryMessage() {
  const send = useSendMessage();

  return useCallback(
    async (chatId: Uuid, messageId: Uuid, deviceId: Uuid) => {
      const pending = usePendingStore.getState();
      const failed = pending.get(chatId, messageId);
      if (!failed) return;
      pending.update(chatId, messageId, { state: 'pending', error: '' });
      await send({
        chatId,
        deviceId,
        messageId,
        text: failed.content,
        reference: failed.reference,
      });
    },
    [send],
  );
}
